import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";

const FIELDS = [
  { key: "views", label: "Views" },
  { key: "likes", label: "Likes" },
  { key: "comments", label: "Comments" },
  { key: "shares", label: "Shares" },
];

// Blank input = null (unknown), shown as "—". Not fabricated as 0.
const toStr = (v) => (v === null || v === undefined ? "" : String(v));

export function EditMetricsDialog({ open, onOpenChange, post, onDone }) {
  const [vals, setVals] = useState({ views: "", likes: "", comments: "", shares: "" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setVals({
        views: toStr(post.views), likes: toStr(post.likes),
        comments: toStr(post.comments), shares: toStr(post.shares),
      });
    }
  }, [open, post]);

  const parseField = (s) => {
    const t = (s ?? "").trim();
    if (t === "") return null;
    const n = parseInt(t.replace(/,/g, ""), 10);
    return Number.isFinite(n) && n >= 0 ? n : null;
  };

  const submit = async () => {
    setSaving(true);
    try {
      const body = {
        views: parseField(vals.views), likes: parseField(vals.likes),
        comments: parseField(vals.comments), shares: parseField(vals.shares),
      };
      const { data } = await api.put(`/posts/${post.id}/metrics`, body);
      toast.success("Metrics saved");
      onOpenChange(false);
      onDone?.(data);
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="edit-metrics-dialog" className="max-w-md rounded-2xl">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Edit Metrics</DialogTitle>
          <DialogDescription>
            @{post.creator_handle?.replace(/^@/, "")} — leave a field blank to mark it unknown ("—"). A refresh won't overwrite a value when its source has none.
          </DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-4">
          {FIELDS.map((f) => (
            <div key={f.key}>
              <Label>{f.label}</Label>
              <Input
                data-testid={`metric-input-${f.key}`}
                value={vals[f.key]}
                inputMode="numeric"
                onChange={(e) => setVals((v) => ({ ...v, [f.key]: e.target.value }))}
                className="mt-1"
                placeholder="—"
              />
            </div>
          ))}
        </div>
        <Button data-testid="submit-edit-metrics" onClick={submit} disabled={saving} className="mt-2 w-full rounded-full bg-[#35606E] py-6 hover:bg-[#294b57]">
          {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Save Metrics
        </Button>
      </DialogContent>
    </Dialog>
  );
}
